import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useSearchParams } from 'react-router';

import {
  chatKeys,
  useChatRoomsQuery,
} from 'modules/ChatV2/api/queries';
import { useChatViewerRole } from 'modules/ChatV2/hooks/useChatViewerRole';
import type { ChatViewerRole, ChatRoomResponse } from 'modules/ChatV2/api/routes';
import { useChatWebSocket } from 'modules/ChatV2/store/ChatWebSocketContext';
import { addNotification } from 'notifications';
import { announceIncoming } from 'utils/desktopNotify';

const bumpRoomUnread = (
  queryClient: ReturnType<typeof useQueryClient>,
  viewerRole: ChatViewerRole,
  chatId: number,
  roomId: string,
) => {
  const patchLists = (status: 'active' | 'closed') => {
    queryClient.setQueryData<ChatRoomResponse[]>(chatKeys.lists(viewerRole, status), (old) => {
      if (!old?.length) {
        return old;
      }
      return old.map((room) => {
        if (room.id === chatId || room.room_id === roomId) {
          return { ...room, unread_count: (room.unread_count ?? 0) + 1 };
        }
        return room;
      });
    });
  };
  patchLists('active');
  patchLists('closed');
};

const clearRoomUnread = (
  queryClient: ReturnType<typeof useQueryClient>,
  viewerRole: ChatViewerRole,
  chatId: number,
  roomId: string,
) => {
  const patchLists = (status: 'active' | 'closed') => {
    queryClient.setQueryData<ChatRoomResponse[]>(chatKeys.lists(viewerRole, status), (old) => {
      if (!old?.length) {
        return old;
      }
      return old.map((room) => {
        if (room.id === chatId || room.room_id === roomId) {
          return { ...room, unread_count: 0 };
        }
        return room;
      });
    });
  };
  patchLists('active');
  patchLists('closed');
};

/**
 * Глобальная подписка на комнаты чатов через WebSocket: бейджи и список без перезагрузки.
 */
export const useRealtimeChatSync = () => {
  const { subscribeRoom, subscribeGlobal, isConnected, userId } = useChatWebSocket();
  const queryClient = useQueryClient();
  const viewerRole = useChatViewerRole();
  const { data: activeRooms } = useChatRoomsQuery(viewerRole, 'active');
  const { data: closedRooms } = useChatRoomsQuery(viewerRole, 'closed', true);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const activeChatId = searchParams.get('chatId');
  const isOnChatPage =
    location.pathname.startsWith('/profile/chats') ||
    location.pathname.startsWith('/teacher/chat') ||
    /^\/profile\/details\/[^/]+\/chats/.test(location.pathname);
  const currentUserId = parseInt(userId || '0', 10);

  const rooms = [...(activeRooms ?? []), ...(closedRooms ?? [])];

  useEffect(() => {
    const unsubscribe = subscribeGlobal((event) => {
      if (event.type === 'notification') {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        void queryClient.invalidateQueries({ queryKey: ['profile-realtime-sync'] });
        const kind = String(event.data?.notification_type || '');
        announceIncoming({
          kind: 'notification',
          title: kind === 'bulletin_available' ? 'Новая заявка' : 'Новое уведомление',
          body: kind === 'bulletin_available' ? 'Появилась заявка для отклика' : 'Откройте колокольчик в кабинете',
        });
        return;
      }
      if (event.type !== 'bulletin_sync') {
        return;
      }
      // Статусы откликов / модерация заявки / закрытие: обновляем списки сразу
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['student_chat_bulletin_threads'] });
      void queryClient.invalidateQueries({ queryKey: ['student_bulletin_tutors'] });
      void queryClient.invalidateQueries({ queryKey: ['my_responses'] });
      void queryClient.invalidateQueries({ queryKey: ['bulletins'] });
      const bulletinId = event.data?.bulletin_id;
      if (bulletinId != null) {
        void queryClient.invalidateQueries({ queryKey: ['bulletin', Number(bulletinId)] });
      }
      void queryClient.invalidateQueries({ queryKey: ['profile-realtime-sync'] });

      const status = event.data?.verification_status;
      if (status === 'approved') {
        addNotification('Заявка одобрена - репетиторы уже могут откликаться', 'success');
        announceIncoming({
          kind: 'notification',
          title: 'Заявка одобрена',
          body: 'Репетиторы уже могут откликаться',
        });
      } else if (status === 'rejected') {
        addNotification('Заявка отклонена модератором', 'error');
        announceIncoming({
          kind: 'notification',
          title: 'Заявка отклонена',
          body: 'Модератор отклонил заявку',
        });
      } else if (status === 'changes_requested') {
        addNotification('Модератор запросил правки по заявке', 'warning');
        announceIncoming({
          kind: 'notification',
          title: 'Нужны правки',
          body: 'Модератор запросил правки по заявке',
        });
      }
    });
    return () => unsubscribe();
  }, [subscribeGlobal, queryClient]);

  useEffect(() => {
    if (!isConnected || !rooms.length || !currentUserId) {
      return;
    }

    const seen = new Set<string>();
    const unsubscribers: Array<() => void> = [];

    for (const room of rooms) {
      if (!room.room_id || seen.has(room.room_id)) {
        continue;
      }
      seen.add(room.room_id);

      const chatId = room.id;
      const roomId = room.room_id;

      const unsubscribe = subscribeRoom(roomId, chatId, (event) => {
        if (event.type === 'message') {
          const senderRaw = event.data.sender;
          const senderId = parseInt(String(senderRaw), 10);
          if (!senderId || senderId === currentUserId) {
            return;
          }

          const viewingThisChat =
            isOnChatPage && activeChatId != null && String(chatId) === activeChatId;

          if (!viewingThisChat) {
            bumpRoomUnread(queryClient, viewerRole, chatId, roomId);
            announceIncoming({
              kind: 'message',
              title: 'Новое сообщение',
              body: String(event.data.text || event.data.preview || 'Вам написали в чат'),
            });
          }
          return;
        }

        if (event.type === 'read') {
          const readerId = parseInt(String(event.data.readerId ?? 0), 10);
          if (readerId !== currentUserId) {
            return;
          }
          clearRoomUnread(queryClient, viewerRole, chatId, roomId);
        }

        if (event.type === 'contact_share') {
          void queryClient.invalidateQueries({ queryKey: chatKeys.contactShare(chatId) });
          void queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'list'] });
        }
      });

      unsubscribers.push(unsubscribe);
    }

    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [
    isConnected,
    rooms,
    currentUserId,
    queryClient,
    isOnChatPage,
    activeChatId,
    viewerRole,
    subscribeRoom,
  ]);
};
